import { useState, useCallback, useEffect, useMemo } from "react";

interface UseTextTruncationProps {
  text: string;
  maxLines: number;
  reservedWidth?:number;
  containerRef: React.RefObject<HTMLElement | null>;
  textRef: React.RefObject<HTMLSpanElement | null>;
}

const mobileBreakpoint = 540;

export const useTextTruncation = ({
  text,
  maxLines,
  reservedWidth = 50,
  containerRef,
  textRef,
}: UseTextTruncationProps) => {
  const [maxChars, setMaxChars] = useState<number>(text.length);

  const calculateMaxChars = useCallback(() => {
    if (!containerRef.current || !textRef.current) return;

    const container = containerRef.current;
    const computedStyle = window.getComputedStyle(textRef.current);

    const lineHeight =
      parseFloat(computedStyle.lineHeight) ||
      parseFloat(computedStyle.fontSize) * 1.2;

    const maxHeight = lineHeight * maxLines;

    const containerWidth = container.offsetWidth - reservedWidth;

    const tempDiv = document.createElement("div");
    tempDiv.style.visibility = "hidden";
    tempDiv.style.position = "absolute";
    tempDiv.style.width = `${containerWidth}px`;
    tempDiv.style.font = computedStyle.font;
    tempDiv.style.lineHeight = computedStyle.lineHeight;
    tempDiv.style.whiteSpace = "normal";
    if (window.innerWidth <= mobileBreakpoint) {
      tempDiv.style.fontSize = "16px";
    } else {
      tempDiv.style.fontSize = "14px";
    }
    tempDiv.style.overflowWrap = "break-word";
    document.body.appendChild(tempDiv);

    const binarySearchMaxChars = () => {
      let low = 0;
      let high = text.length;
      let result = text.length;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        tempDiv.textContent = text.slice(0, mid) + "...";
        const textHeight = tempDiv.offsetHeight;

        if (textHeight <= maxHeight) {
          low = mid + 1;
          result = mid;
        } else {
          high = mid - 1;
        }
      }
      return result;
    };

    const tempMaxChars = binarySearchMaxChars();
    document.body.removeChild(tempDiv);
    setMaxChars(tempMaxChars);
  }, [text, maxLines, containerRef, textRef,reservedWidth]);

  useEffect(() => {
    const handleResize = () => {
      calculateMaxChars();
    };

    calculateMaxChars();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [calculateMaxChars]);

  const isTruncated = useMemo(() => {
    return text.length > maxChars;
  }, [text, maxChars]);

  return { maxChars, isTruncated };
};